/**
 * data/brokerFeeCatalog.ts
 * Heavy broker fee configuration data
 * 
 * Contains detailed fee structures loaded on-demand when user
 * views fee details or configures a specific broker.
 * Separated from selection metadata for performance optimization.
 */

import type { BrokerFeeCatalogEntry, BrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';

/**
 * Heavy broker fee configuration data by broker ID
 * 
 * Lazy-loaded when user clicks "View Fees" or selects broker for configuration.
 * Contains comprehensive fee structures, metadata, and verification status.
 */
export const BROKER_FEE_CATALOG_DATA = new Map<string, BrokerFeeCatalogEntry>([
  ['admiral-markets-trade', {
  "brokerId": "admiral-markets-trade",
  "lastUpdated": "2025-08-18T03:39:01.817Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD",
      "EUR",
      "GBP",
      "CHF",
      "PLN"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": true,
    "minimumBalance": {
      "amount": 10000,
      "precision": 2,
      "currency": "USD"
    },
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "inactivityFee": 1000,
      "notes": "MT4/MT5 platforms included free. €10/month inactivity after 24 months."
    }
  },
  "feeConfigurations": [
    {
      "id": "admiral-forex-majors",
      "name": "Forex Major Pairs",
      "category": {
        "assetClass": "forex",
        "marketSegment": "majors"
      },
      "appliesTo": [
        "EUR/USD",
        "GBP/USD",
        "USD/JPY",
        "USD/CHF",
        "AUD/USD",
        "USD/CAD",
        "NZD/USD"
      ],
      "commission": {
        "feeType": "spreadOnly",
        "feeAmount": 0,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "template"
      },
      "overnightFinancing": {
        "benchmark": "SOFR",
        "adminSpread": 250,
        "adminSpreadPrecision": 2,
        "frequency": "daily",
        "assetClass": "forex",
        "hasTripleCharges": true,
        "tripleChargeDay": "wednesday",
        "verificationStatus": "template",
        "verificationNotes": "Benchmark ± 2.5% admin spread, triple on Wednesday"
      },
      "currencyConversion": {
        "rateType": "spotPlusMarkup",
        "triggers": [
          "trading"
        ],
        "markup": 100,
        "markupPrecision": 2,
        "usesRealTimeRates": true,
        "verificationStatus": "template",
        "verificationNotes": "1% conversion fee between base currencies"
      },
      "notes": "Spread-only pricing, no explicit commission",
      "status": "active"
    },
    {
      "id": "admiral-us-stocks-cfd",
      "name": "US Stock CFDs",
      "category": {
        "assetClass": "cfd",
        "marketSegment": "us-equities"
      },
      "appliesTo": [
        "US Stocks"
      ],
      "commission": {
        "feeType": "perShare",
        "feeAmount": 2,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 100,
        "minPerOrderPrecision": 2,
        "verificationStatus": "template"
      },
      "overnightFinancing": {
        "benchmark": "SOFR",
        "adminSpread": 250,
        "adminSpreadPrecision": 2,
        "frequency": "daily",
        "assetClass": "stocks",
        "hasTripleCharges": true,
        "tripleChargeDay": "friday",
        "verificationStatus": "template",
        "verificationNotes": "SOFR ± 2.5% for stock CFDs, triple on Friday"
      },
      "currencyConversion": {
        "rateType": "spotPlusMarkup",
        "triggers": [
          "trading"
        ],
        "markup": 100,
        "markupPrecision": 2,
        "usesRealTimeRates": true,
        "verificationStatus": "template"
      },
      "notes": "Commission charged per share for US stock CFDs",
      "status": "active"
    },
    {
      "id": "admiral-eu-stocks-cfd",
      "name": "EU Stock CFDs",
      "category": {
        "assetClass": "cfd",
        "marketSegment": "eu-equities"
      },
      "appliesTo": [
        "EU Stocks"
      ],
      "commission": {
        "feeType": "percentage",
        "feeAmount": 10,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 100,
        "minPerOrderPrecision": 2,
        "verificationStatus": "template"
      },
      "overnightFinancing": {
        "benchmark": "ESTR",
        "adminSpread": 250,
        "adminSpreadPrecision": 2,
        "frequency": "daily",
        "assetClass": "stocks",
        "hasTripleCharges": true,
        "tripleChargeDay": "friday",
        "verificationStatus": "template",
        "verificationNotes": "€STR ± 2.5% for EU stocks"
      },
      "notes": "Percentage-based commission for EU stock CFDs",
      "status": "active"
    },
    {
      "id": "admiral-indices-cfd",
      "name": "Index CFDs",
      "category": {
        "assetClass": "cfd",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "DAX40",
        "UK100",
        "US500",
        "NASDAQ100"
      ],
      "commission": {
        "feeType": "spreadOnly",
        "feeAmount": 0,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "template"
      },
      "overnightFinancing": {
        "benchmark": "SOFR",
        "adminSpread": 245,
        "adminSpreadPrecision": 2,
        "frequency": "daily",
        "assetClass": "indices",
        "hasTripleCharges": true,
        "tripleChargeDay": "friday",
        "verificationStatus": "template",
        "verificationNotes": "Benchmark ± admin spread, triple on Friday for indices"
      },
      "notes": "Spread-only pricing, no explicit commission",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "third-party",
    "sourceReference": "recovered_broker_data.ts",
    "verifiedAt": "2025-08-18",
    "notes": "Migrated from comprehensive broker data during architecture refactor"
  }
}],

  ['tradovate-personal-free', {
  "brokerId": "tradovate-personal-free",
  "lastUpdated": "2025-08-18T03:39:01.817Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false,
    "minimumBalance": {
      "amount": 0,
      "precision": 2,
      "currency": "USD"
    },
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "inactivityFee": 3500,
      "notes": "Free plan with pay-as-you-trade model. $35/month inactivity fee if logged in but no trades. Top-of-book data included."
    }
  },
  "feeConfigurations": [
    {
      "id": "tradovate-indices-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES",
        "MNQ",
        "MYM",
        "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 95,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro E-mini index futures with all-in pricing including exchange, clearing, and NFA fees",
      "status": "active"
    },
    {
      "id": "tradovate-indices-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES",
        "NQ",
        "YM",
        "RTY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 288,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard E-mini index futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "tradovate-indices-nano",
      "name": "Nano Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "B5",
        "LB5",
        "TEC",
        "LTEC"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 43,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Nano index futures including Coinbase and crypto indices",
      "status": "active"
    },
    {
      "id": "tradovate-metals-standard",
      "name": "Standard Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC",
        "SI",
        "HG",
        "PL",
        "PA"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 310,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard metals futures: Gold, Silver, Copper, Platinum, Palladium",
      "status": "active"
    },
    {
      "id": "tradovate-metals-micro",
      "name": "Micro Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC",
        "SIL",
        "MHG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 110,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro metals futures with lower contract sizes",
      "status": "active"
    },
    {
      "id": "tradovate-energies-standard",
      "name": "Standard Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL",
        "NG",
        "HO",
        "RB",
        "BZ"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 300,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard energy futures: Crude Oil, Natural Gas, Heating Oil, RBOB, Brent",
      "status": "active"
    },
    {
      "id": "tradovate-energies-micro",
      "name": "Micro Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL",
        "QM",
        "QG",
        "QH"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 110,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro energy futures with smaller contract sizes",
      "status": "active"
    },
    {
      "id": "tradovate-commodities-grains",
      "name": "Agricultural Commodity Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "ZC",
        "ZS",
        "ZW",
        "ZL",
        "ZM",
        "ZO",
        "ZR"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 360,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Agricultural futures: Corn, Soybeans, Wheat, Soybean Oil/Meal, Oats, Rice",
      "status": "active"
    },
    {
      "id": "tradovate-financials",
      "name": "Financial Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZF",
        "ZT",
        "ZN",
        "ZB",
        "UB",
        "TN",
        "TWE"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 215,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Treasury futures: 2Y, 5Y, 10Y, 30Y bonds and notes",
      "status": "active"
    },
    {
      "id": "tradovate-currencies",
      "name": "Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6E",
        "6B",
        "6A",
        "6C",
        "6J",
        "6S",
        "6N",
        "6M",
        "6L"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 310,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Currency futures: EUR, GBP, AUD, CAD, JPY, CHF, NZD, MXN, BRL",
      "status": "active"
    },
    {
      "id": "tradovate-meats",
      "name": "Livestock Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "meats"
      },
      "appliesTo": [
        "LE",
        "GF",
        "HE"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 360,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Livestock futures: Live Cattle, Feeder Cattle, Lean Hogs",
      "status": "active"
    },
    {
      "id": "tradovate-softs",
      "name": "Soft Commodity Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "softs"
      },
      "appliesTo": [
        "KC",
        "SB",
        "CC",
        "CT",
        "OJ",
        "LBS"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 360,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Soft commodity futures: Coffee, Sugar, Cocoa, Cotton, Orange Juice, Lumber",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "third-party",
    "sourceReference": "recovered_broker_data.ts",
    "verifiedAt": "2025-08-18",
    "notes": "Migrated from comprehensive broker data during architecture refactor"
  }
}],

  ['tradovate-personal-monthly', {
  "brokerId": "tradovate-personal-monthly",
  "lastUpdated": "2025-08-18T03:39:01.817Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false,
    "minimumBalance": {
      "amount": 0,
      "precision": 2,
      "currency": "USD"
    },
    "platformFees": {
      "monthlyFee": 9900,
      "dataFees": 0,
      "inactivityFee": 0,
      "notes": "Monthly subscription plan with reduced commission rates. $99/month subscription fee. Top-of-book data included."
    }
  },
  "feeConfigurations": [
    {
      "id": "tradovate-monthly-indices-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES",
        "MNQ",
        "MYM",
        "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 85,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro E-mini index futures with reduced commission rates on Monthly plan",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-indices-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES",
        "NQ",
        "YM",
        "RTY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 258,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard E-mini index futures with reduced commission rates on Monthly plan",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-indices-nano",
      "name": "Nano Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "B5",
        "LB5",
        "TEC",
        "LTEC"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 38,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Nano index futures including Coinbase and crypto indices with reduced rates",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-metals-standard",
      "name": "Standard Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC",
        "SI",
        "HG",
        "PL",
        "PA"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 280,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard metals futures with reduced commission rates on Monthly plan",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-metals-micro",
      "name": "Micro Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC",
        "SIL",
        "MHG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 100,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro metals futures with reduced commission rates on Monthly plan",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-energies-standard",
      "name": "Standard Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL",
        "NG",
        "HO",
        "RB",
        "BZ"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 270,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard energy futures with reduced commission rates on Monthly plan",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-energies-micro",
      "name": "Micro Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL",
        "QM",
        "QG",
        "QH"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 100,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro energy futures with reduced commission rates on Monthly plan",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-financials",
      "name": "Financial Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZF",
        "ZT",
        "ZN",
        "ZB",
        "UB",
        "TN",
        "TWE",
        "GE",
        "ZQ",
        "10Y",
        "2YY",
        "30Y",
        "5YY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 185,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Financial futures including Treasury bonds, notes, and micro yields with reduced rates",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-grains",
      "name": "Grain & Agricultural Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "ZC",
        "ZS",
        "ZW",
        "ZM",
        "ZL",
        "ZO",
        "ZR",
        "XC",
        "XW",
        "XK"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 330,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Grain and agricultural futures with reduced commission rates on Monthly plan",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-currencies",
      "name": "Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6E",
        "6B",
        "6A",
        "6C",
        "6J",
        "6S",
        "6N",
        "6L",
        "6M",
        "E7",
        "J7",
        "M6A",
        "M6E",
        "M6B",
        "M6C",
        "MSF",
        "MJY",
        "MCD",
        "DX"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 205,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Currency futures including majors, E-mini, and micro contracts with reduced rates",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-meats",
      "name": "Livestock Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "meats"
      },
      "appliesTo": [
        "LE",
        "GF",
        "HE"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 330,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Livestock futures: Live Cattle, Feeder Cattle, Lean Hogs with reduced rates",
      "status": "active"
    },
    {
      "id": "tradovate-monthly-softs",
      "name": "Soft Commodity Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "softs"
      },
      "appliesTo": [
        "KC",
        "SB",
        "CC",
        "CT",
        "OJ",
        "LBS"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 330,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Soft commodity futures: Coffee, Sugar, Cocoa, Cotton, Orange Juice, Lumber with reduced rates",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "third-party",
    "sourceReference": "recovered_broker_data.ts",
    "verifiedAt": "2025-08-18",
    "notes": "Migrated from comprehensive broker data during architecture refactor"
  }
}],

  ['tradovate-personal-lifetime', {
  "brokerId": "tradovate-personal-lifetime",
  "lastUpdated": "2025-08-18T03:39:01.817Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false,
    "minimumBalance": {
      "amount": 0,
      "precision": 2,
      "currency": "USD"
    },
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "inactivityFee": 0,
      "notes": "Lifetime plan with lowest commission rates. One-time $3,200 lifetime fee. Top-of-book data included."
    }
  },
  "feeConfigurations": [
    {
      "id": "tradovate-lifetime-indices-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES",
        "MNQ",
        "MYM",
        "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 65,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro E-mini index futures with lowest commission rates on Lifetime plan",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-indices-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES",
        "NQ",
        "YM",
        "RTY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 218,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard E-mini index futures with lowest commission rates on Lifetime plan",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-indices-nano",
      "name": "Nano Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "B5",
        "LB5",
        "TEC",
        "LTEC"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 28,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Nano index futures including Coinbase and crypto indices with lowest rates",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-metals-standard",
      "name": "Standard Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC",
        "SI",
        "HG",
        "PL",
        "PA"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 240,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard metals futures with lowest commission rates on Lifetime plan",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-metals-micro",
      "name": "Micro Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC",
        "SIL",
        "MHG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 80,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro metals futures with lowest commission rates on Lifetime plan",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-energies-standard",
      "name": "Standard Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL",
        "NG",
        "HO",
        "RB",
        "BZ"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 230,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Standard energy futures with lowest commission rates on Lifetime plan",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-energies-micro",
      "name": "Micro Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL",
        "QM",
        "QG",
        "QH"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 80,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro energy futures with lowest commission rates on Lifetime plan",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-financials",
      "name": "Financial Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZF",
        "ZT",
        "ZN",
        "ZB",
        "UB",
        "TN",
        "TWE",
        "GE",
        "ZQ",
        "10Y",
        "2YY",
        "30Y",
        "5YY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 145,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Financial futures including Treasury bonds, notes, and micro yields with lowest rates",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-grains",
      "name": "Grain & Agricultural Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "ZC",
        "ZS",
        "ZW",
        "ZM",
        "ZL",
        "ZO",
        "ZR",
        "XC",
        "XW",
        "XK"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 290,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Grain and agricultural futures with lowest commission rates on Lifetime plan",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-currencies",
      "name": "Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6E",
        "6B",
        "6A",
        "6C",
        "6J",
        "6S",
        "6N",
        "6L",
        "6M",
        "E7",
        "J7",
        "M6A",
        "M6E",
        "M6B",
        "M6C",
        "MSF",
        "MJY",
        "MCD",
        "DX"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 165,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Currency futures including majors, E-mini, and micro contracts with lowest rates",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-meats",
      "name": "Livestock Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "meats"
      },
      "appliesTo": [
        "LE",
        "GF",
        "HE"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 290,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Livestock futures: Live Cattle, Feeder Cattle, Lean Hogs with lowest rates",
      "status": "active"
    },
    {
      "id": "tradovate-lifetime-softs",
      "name": "Soft Commodity Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "softs"
      },
      "appliesTo": [
        "KC",
        "SB",
        "CC",
        "CT",
        "OJ",
        "LBS"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 290,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Soft commodity futures: Coffee, Sugar, Cocoa, Cotton, Orange Juice, Lumber with lowest rates",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "third-party",
    "sourceReference": "recovered_broker_data.ts",
    "verifiedAt": "2025-08-18",
    "notes": "Migrated from comprehensive broker data during architecture refactor"
  }
}],

  ['interactive-brokers-pro-usa', {
  "brokerId": "interactive-brokers-pro-usa",
  "lastUpdated": "2025-08-18T03:39:01.817Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD",
      "EUR",
      "GBP",
      "CAD",
      "AUD",
      "CHF",
      "JPY",
      "HKD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": true,
    "minimumBalance": {
      "amount": 0,
      "precision": 2,
      "currency": "USD"
    },
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "inactivityFee": 0,
      "notes": "No platform fees. Basic market data included, premium data requires subscription. US-focused with USD denomination."
    }
  },
  "feeConfigurations": [
    {
      "id": "ibkr-us-stocks-fixed",
      "name": "US Stocks/ETFs (Fixed)",
      "category": {
        "assetClass": "stocks",
        "marketSegment": "us-equities"
      },
      "appliesTo": [
        "US Stocks",
        "US ETFs"
      ],
      "commission": {
        "feeType": "perShare",
        "feeAmount": 0.5,
        "feePrecision": 3,
        "chargeOn": "bothLegs",
        "minPerOrder": 100,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "Fixed pricing structure - simple and predictable",
      "status": "active"
    },
    {
      "id": "ibkr-us-stocks-tiered",
      "name": "US Stocks/ETFs (Tiered)",
      "category": {
        "assetClass": "stocks",
        "marketSegment": "us-equities"
      },
      "appliesTo": [
        "US Stocks",
        "US ETFs"
      ],
      "commission": {
        "feeType": "perShare",
        "feeAmount": 0.35,
        "feePrecision": 3,
        "chargeOn": "bothLegs",
        "minPerOrder": 35,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "Tiered pricing - lower base rate plus exchange fees",
      "status": "active"
    },
    {
      "id": "ibkr-options-tiered",
      "name": "Options (Tiered)",
      "category": {
        "assetClass": "options",
        "marketSegment": "us-equities"
      },
      "appliesTo": [
        "US Options"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 65,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 100,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "Equity options with premium-based pricing and volume discounts",
      "status": "active"
    },
    {
      "id": "ibkr-options-index",
      "name": "Index Options",
      "category": {
        "assetClass": "options",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "SPX",
        "NDX",
        "RUT",
        "VIX",
        "Index Options"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 65,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 100,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "Index options with volume-based tiered pricing",
      "status": "active"
    },
    {
      "id": "ibkr-options-nanos",
      "name": "NANOS Options",
      "category": {
        "assetClass": "options",
        "marketSegment": "us-equities"
      },
      "appliesTo": [
        "NANOS"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 6.5,
        "feePrecision": 3,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "NANOS options with special flat-rate pricing, no minimum per order",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-indices-micro",
      "name": "US Index Futures (Micro & E-mini)",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES",
        "MNQ",
        "M2K",
        "MYM",
        "2YY",
        "5YY",
        "10Y",
        "30Y",
        "VOLQ",
        "VXM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 25,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Spot-quoted futures, E-micro futures including indices and yield products",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-metals",
      "name": "US Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC",
        "SIL",
        "MWN",
        "MTN"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 25,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro metals futures including micro gold, silver, and platinum",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-energies",
      "name": "US Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL",
        "MRB",
        "MHNG",
        "MHO"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 25,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro energy futures with tiered volume discounts",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-commodities",
      "name": "US Agricultural Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "MZC",
        "MZS",
        "MZW",
        "MZL",
        "MZM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 25,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Micro agricultural futures: corn, soybeans, wheat, soybean oil/meal",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-currencies",
      "name": "US FX Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "E-mini FX",
        "MNK",
        "MNI"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 50,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "E-mini FX futures with volume-based tiered pricing",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-crypto-bitcoin",
      "name": "Bitcoin Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "BTC",
        "MBT",
        "BFF"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 500,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "CME Bitcoin futures with separate pricing for standard, micro, and Friday contracts",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-crypto-ethereum",
      "name": "Ethereum Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "ETH",
        "MET"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 300,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "CME Ethereum futures with separate pricing for standard and micro contracts",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-crypto-other",
      "name": "Other Crypto Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "SOL",
        "MSL",
        "XRP",
        "MXP"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 500,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Solana and Ripple futures with standard and micro contract sizes",
      "status": "active"
    },
    {
      "id": "ibkr-us-futures-small-exchange",
      "name": "Small Exchange Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "Small Exchange Contracts"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 8,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "verificationStatus": "verified"
      },
      "notes": "Small Exchange futures with simplified flat-rate pricing",
      "status": "active"
    },
    {
      "id": "ibkr-us-bonds-treasuries",
      "name": "US Treasury Securities",
      "category": {
        "assetClass": "bonds",
        "marketSegment": "government"
      },
      "appliesTo": [
        "Treasury Bills",
        "Treasury Notes",
        "Treasury Bonds"
      ],
      "commission": {
        "feeType": "percentage",
        "feeAmount": 2,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 500,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "US Treasury Bills, Notes, and Bonds with tiered percentage pricing",
      "status": "active"
    },
    {
      "id": "ibkr-us-bonds-corporate",
      "name": "US Corporate Bonds & CDs",
      "category": {
        "assetClass": "bonds",
        "marketSegment": "corporate"
      },
      "appliesTo": [
        "Corporate Bonds",
        "Certificate of Deposits"
      ],
      "commission": {
        "feeType": "percentage",
        "feeAmount": 100,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 100,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "US Corporate bonds and CDs with tiered percentage pricing",
      "status": "active"
    },
    {
      "id": "ibkr-us-bonds-municipal",
      "name": "US Municipal Bonds",
      "category": {
        "assetClass": "bonds",
        "marketSegment": "government"
      },
      "appliesTo": [
        "Municipal Bonds",
        "State Bonds",
        "Local Government Bonds"
      ],
      "commission": {
        "feeType": "percentage",
        "feeAmount": 50,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 100,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "US Municipal bonds with tiered percentage pricing",
      "status": "active"
    },
    {
      "id": "ibkr-forex",
      "name": "Forex",
      "category": {
        "assetClass": "forex",
        "marketSegment": "majors"
      },
      "appliesTo": [
        "Currency Pairs"
      ],
      "commission": {
        "feeType": "percentage",
        "feeAmount": 20,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 200,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "Commission-based FX with transparent pricing from 17 major dealers, volume-based tiered rates",
      "status": "active"
    },
    {
      "id": "ibkr-crypto",
      "name": "Cryptocurrency",
      "category": {
        "assetClass": "crypto",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "Bitcoin",
        "Ethereum",
        "Other Crypto"
      ],
      "commission": {
        "feeType": "percentage",
        "feeAmount": 18,
        "feePrecision": 2,
        "chargeOn": "bothLegs",
        "minPerOrder": 175,
        "minPerOrderPrecision": 2,
        "verificationStatus": "verified"
      },
      "notes": "Cryptocurrency trading with volume discounts",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "third-party",
    "sourceReference": "recovered_broker_data.ts",
    "verifiedAt": "2025-08-18",
    "notes": "Migrated from comprehensive broker data during architecture refactor"
  }
}],

  ['apex-tradovate', {
  "brokerId": "apex-tradovate",
  "lastUpdated": "2025-08-18T03:39:01.817Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false
  },
  "feeConfigurations": [
    {
      "id": "apex-equity-indices-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES",
        "NQ",
        "YM",
        "RTY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 155,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Standard E-mini equity index futures with comprehensive all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-equity-indices-midcap",
      "name": "E-mini Midcap 400 Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "EMD"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 150,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "E-mini S&P Midcap 400 futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-equity-indices-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES",
        "MNQ",
        "MYM",
        "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 52,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Micro E-mini equity index futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-currencies-major",
      "name": "Major Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6A",
        "6B",
        "6J",
        "6C",
        "6S",
        "6E",
        "6N"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 177,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Major currency futures with comprehensive all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-currencies-micro",
      "name": "E-Micro Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "M6A",
        "M6E"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 41,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "E-Micro currency futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-energy-petroleum",
      "name": "Petroleum Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL",
        "RB",
        "HO"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 167,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Petroleum energy futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-energy-gas",
      "name": "Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "NG",
        "QG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 177,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Natural gas futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-metals-precious",
      "name": "Precious Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC",
        "SI",
        "HG",
        "PL",
        "PA",
        "QO",
        "QI",
        "MGC"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 177,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Precious metals futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-commodities-agricultural",
      "name": "Agricultural Commodities Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "HE",
        "LE",
        "GF",
        "ZS",
        "ZL",
        "ZC",
        "ZW",
        "ZM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 227,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Agricultural commodities futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-crypto-digital",
      "name": "Cryptocurrency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MBT",
        "MET"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 267,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Cryptocurrency futures with all-in pricing",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "third-party",
    "sourceReference": "recovered_broker_data.ts",
    "verifiedAt": "2025-08-18",
    "notes": "Migrated from comprehensive broker data during architecture refactor"
  }
}],

  ['apex-rithmic', {
  "brokerId": "apex-rithmic",
  "lastUpdated": "2025-08-19T00:00:00.000Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false,
    "platformFees": {
      "monthlyFee": 8500,
      "dataFees": 0,
      "notes": "$85/month includes NinjaTrader platform ($75 value) and real-time data ($55 value). Lifetime activation options available: $140-$340 depending on account size."
    }
  },
  "feeConfigurations": [
    {
      "id": "apex-rithmic-equity-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES",
        "NQ",
        "YM",
        "RTY",
        "NKD"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 199,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Standard E-mini equity index futures. All-in rate including exchange, clearing, and NFA fees.",
      "status": "active"
    },
    {
      "id": "apex-rithmic-equity-midcap",
      "name": "E-mini Midcap 400 Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "EMD"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 199,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "E-mini S&P Midcap 400 futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-equity-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES",
        "MNQ",
        "MYM",
        "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 51,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Micro E-mini equity index futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-currencies-major",
      "name": "Major Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6A",
        "6B",
        "6C",
        "6E",
        "6J",
        "6S",
        "6N"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 236,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Major currency futures including Australian Dollar, British Pound, Canadian Dollar, Euro FX, Japanese Yen, Swiss Franc, New Zealand Dollar",
      "status": "active"
    },
    {
      "id": "apex-rithmic-currencies-micro",
      "name": "E-Micro Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "M6A",
        "M6E",
        "M6J"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 42,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "E-Micro currency futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-agricultural",
      "name": "Agricultural Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "HE",
        "LE",
        "GF",
        "ZC",
        "ZW",
        "ZS",
        "ZM",
        "ZL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 279,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Agricultural futures including Lean Hogs, Live Cattle, Feeder Cattle, Corn, Wheat, Soybeans, Soybean Meal, Soybean Oil",
      "status": "active"
    },
    {
      "id": "apex-rithmic-energy-crude",
      "name": "Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 198,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Crude Oil futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-energy-mini-crude",
      "name": "Mini Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 196,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Mini Crude Oil futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-energy-micro-crude",
      "name": "Micro Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 51,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Micro Crude Oil futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-energy-gas",
      "name": "Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "NG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 198,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Natural Gas futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-energy-mini-gas",
      "name": "E-mini Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 126,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "E-mini Natural Gas futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-energy-refined",
      "name": "Refined Energy Products",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "HO",
        "RB"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 198,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Heating Oil and RBOB Gasoline futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-metals-precious",
      "name": "Precious Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC",
        "SI",
        "PL",
        "PA"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 231,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Gold, Silver, Platinum, and Palladium futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-metals-base",
      "name": "Base Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "HG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 231,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Copper futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-metals-mini",
      "name": "miNY Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "QI",
        "QO"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 151,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "miNY Silver and miNY Gold futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-metals-micro-gold",
      "name": "E-Micro Gold Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 76,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "E-Micro Gold futures with all-in pricing",
      "status": "active"
    },
    {
      "id": "apex-rithmic-crypto-bitcoin",
      "name": "Micro Bitcoin Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MBT"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 276,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Micro Bitcoin futures with all-in pricing. Note: Mini BT is not supported.",
      "status": "active"
    },
    {
      "id": "apex-rithmic-crypto-ethereum",
      "name": "Micro Ethereum Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MET"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 46,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Micro Ethereum futures with all-in pricing. Note: Mini ET is not supported.",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "official-website",
    "sourceReference": "Rithmic-Apex.md Detailed Commissions Report",
    "verifiedAt": "2025-08-19",
    "notes": "Commission rates from official Apex support documentation for Rithmic platform. All rates are per side and include exchange, clearing, and NFA fees."
  }
}],

  ['tradeify-tradovate', {
  "brokerId": "tradeify-tradovate",
  "lastUpdated": "2025-08-18T03:39:01.818Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false
  },
  "feeConfigurations": [
    {
      "id": "tradeify-equity-indices-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES",
        "NQ",
        "YM",
        "RTY",
        "EMD"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 288,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Standard E-mini equity index futures with total all-in costs",
      "status": "active"
    },
    {
      "id": "tradeify-equity-indices-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES",
        "MNQ",
        "MYM",
        "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 91,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Micro E-mini equity index futures with total all-in costs",
      "status": "active"
    },
    {
      "id": "tradeify-currencies-major",
      "name": "Major Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6A",
        "6B",
        "6J",
        "6C",
        "6E",
        "6S"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 310,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Major currency futures with total all-in costs",
      "status": "active"
    },
    {
      "id": "tradeify-currencies-micro",
      "name": "E-Micro Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "M6A",
        "M6E"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 80,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "E-Micro currency futures with total all-in costs",
      "status": "active"
    },
    {
      "id": "tradeify-energy-petroleum",
      "name": "Petroleum Energy Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL",
        "RB",
        "HO",
        "QM",
        "MCL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 270,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Petroleum energy futures with contract-specific total all-in costs",
      "status": "active"
    },
    {
      "id": "tradeify-energy-gas",
      "name": "Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "NG",
        "QG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 250,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Natural gas futures with contract-specific total all-in costs",
      "status": "active"
    },
    {
      "id": "tradeify-metals-precious",
      "name": "Precious Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC",
        "SI",
        "HG",
        "QO",
        "QI",
        "MGC"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 290,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Precious metals futures with contract-specific total all-in costs",
      "status": "active"
    },
    {
      "id": "tradeify-commodities-agricultural",
      "name": "Agricultural Commodities Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "HE",
        "LE",
        "GF",
        "ZC",
        "ZS",
        "ZL",
        "ZW",
        "ZM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 580,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Agricultural commodities futures with contract-specific total all-in costs",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "third-party",
    "sourceReference": "recovered_broker_data.ts",
    "verifiedAt": "2025-08-18",
    "notes": "Migrated from comprehensive broker data during architecture refactor"
  }
}],

  ['ftmo', {
  "brokerId": "ftmo",
  "lastUpdated": "2025-08-19T00:00:00.000Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD",
      "EUR", 
      "GBP",
      "CAD",
      "AUD",
      "CHF",
      "CZK"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": true,
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "notes": "No platform fees - included in challenge fee. Challenge fees are refunded upon first profit withdrawal. Swap fees vary by instrument - check platform for details. Users can customize instrument-specific fees after setup."
    }
  },
  "feeConfigurations": [
    {
      "id": "ftmo-forex-all",
      "name": "All Forex Pairs",
      "category": {
        "assetClass": "forex",
        "marketSegment": "currencies"
      },
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 300,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All forex pairs charged $3.00 per standard lot round-trip. Includes major, minor, and exotic pairs.",
      "status": "active"
    },
    {
      "id": "ftmo-metals-cfd",
      "name": "Precious Metals CFDs",
      "category": {
        "assetClass": "cfd",
        "marketSegment": "metals"
      },
      "commission": {
        "feeType": "percentage",
        "feeAmount": 10,
        "feePrecision": 4,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Precious metals (Gold, Silver, etc.) charged 0.0010% commission per lot.",
      "status": "active"
    },
    {
      "id": "ftmo-equity-cfd",
      "name": "Equity CFDs",
      "category": {
        "assetClass": "stocks",
        "marketSegment": "us-equities"
      },
      "commission": {
        "feeType": "percentage",
        "feeAmount": 40,
        "feePrecision": 4,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "US equity CFDs charged 0.0040% commission per lot.",
      "status": "active"
    },
    {
      "id": "ftmo-indices-cfd",
      "name": "Index CFDs",
      "category": {
        "assetClass": "cfd",
        "marketSegment": "indices"
      },
      "commission": {
        "feeType": "spreadOnly",
        "feeAmount": 0,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Index CFDs have zero commission - cost is in spread only.",
      "status": "active"
    },
    {
      "id": "ftmo-crypto-cfd",
      "name": "Cryptocurrency CFDs",
      "category": {
        "assetClass": "crypto",
        "marketSegment": "digital-assets"
      },
      "commission": {
        "feeType": "spreadOnly",
        "feeAmount": 0,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Cryptocurrency CFDs have zero commission - cost is in spread only.",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "official-website",
    "sourceReference": "MT5-FTMO-Standard.md, cTrader-FTMO-Standard.md, MT4-FTMO-Standard.md research files",
    "verifiedAt": "2025-08-19",
    "notes": "Commission rates from FTMO research across MT4, MT5, and cTrader platforms. All platforms have identical fee structures."
  }
}],

  ['topstep-x', {
  "brokerId": "topstep-x",
  "lastUpdated": "2025-01-19T00:00:00.000Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false,
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 3425,
      "notes": "TopStepX proprietary platform included free. Level 1 data free, Level 2 (DOM) optional at $34.25/month."
    }
  },
  "feeConfigurations": [
    {
      "id": "topstep-x-equity-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES", "NQ", "YM", "RTY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 280,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free platform. Amount shown is NFA ($0.04) + Exchange ($2.76) fees only for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-equity-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES", "MNQ", "MYM", "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 74,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($0.70) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-equity-nikkei",
      "name": "Nikkei Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "NKD"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 434,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($4.30) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-currencies-major",
      "name": "Major Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6A", "6B", "6C", "6E", "6J", "6S", "6N", "6M"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 324,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($3.20) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-currencies-emini",
      "name": "E-mini Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "E7"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 174,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.70) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-currencies-micro",
      "name": "Micro Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "M6A", "M6B", "M6E"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 52,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($0.48) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-energy-crude",
      "name": "Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 304,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($3.00) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-energy-micro-crude",
      "name": "Micro Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 104,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.00) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-energy-emini-crude",
      "name": "E-mini Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 244,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($2.40) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-energy-gas",
      "name": "Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "NG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 324,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($3.20) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-energy-emini-gas",
      "name": "E-mini Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 104,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.00) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-energy-micro-gas",
      "name": "Micro Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MNG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 124,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.20) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-energy-refined",
      "name": "Refined Products Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "RB", "HO"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 304,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($3.00) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-metals-precious",
      "name": "Precious Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC", "SI", "PL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 324,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($3.20) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-metals-base",
      "name": "Base Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "HG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 324,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($3.20) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-metals-micro",
      "name": "Micro Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC", "MHG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 124,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.20) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-metals-micro-silver",
      "name": "Micro Silver Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "SIL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 204,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($2.00) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-agricultural-grains",
      "name": "Grain Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "ZC", "ZW", "ZS", "ZM", "ZL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 430,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($4.26) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-agricultural-meats",
      "name": "Livestock Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "HE", "LE"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 424,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($4.20) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-financials-notes",
      "name": "Treasury Note Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZT", "ZF"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 134,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.30) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-financials-10yr",
      "name": "10-Year Treasury Note Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZN", "TN"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 164,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.60) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-financials-bonds",
      "name": "Treasury Bond Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZB"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 178,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.74) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-financials-ultra",
      "name": "Ultra Treasury Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "UB"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 194,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($1.90) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-crypto-bitcoin",
      "name": "Micro Bitcoin Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MBT"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 234,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($2.30) fees for round-trip.",
      "status": "active"
    },
    {
      "id": "topstep-x-crypto-ethereum",
      "name": "Micro Ethereum Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MET"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 24,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "Commission-free. NFA ($0.04) + Exchange ($0.20) fees for round-trip.",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "official-website",
    "sourceReference": "TopStepX-CommissionFree.md research file",
    "verifiedAt": "2025-01-19",
    "notes": "TopStepX proprietary platform with commission-free trading. Only NFA and clearing fees apply."
  }
}],

  ['topstep-tradovate', {
  "brokerId": "topstep-tradovate",
  "lastUpdated": "2025-01-19T00:00:00.000Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false,
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "notes": "Tradovate or TradingView platform included. CQG data feed included."
    }
  },
  "feeConfigurations": [
    {
      "id": "topstep-tradovate-equity-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES", "NQ", "YM", "RTY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 428,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $2.76 exchange = $4.28 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-equity-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES", "MNQ", "MYM", "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 134,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $0.30 commission + $0.30 clearing + $0.04 NFA + $0.70 exchange = $1.34 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-equity-nikkei",
      "name": "Nikkei Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "NKD"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 582,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $4.30 exchange = $5.82 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-currencies-major",
      "name": "Major Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6A", "6B", "6C", "6E", "6J", "6S", "6N", "6M"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 472,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $3.20 exchange = $4.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-currencies-emini",
      "name": "E-mini Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "E7"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 322,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.70 exchange = $3.22 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-currencies-micro",
      "name": "Micro Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "M6A", "M6B", "M6E"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 112,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $0.30 commission + $0.30 clearing + $0.04 NFA + $0.48 exchange = $1.12 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-energy-crude",
      "name": "Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 452,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $3.00 exchange = $4.52 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-energy-micro-crude",
      "name": "Micro Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 164,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $0.30 commission + $0.30 clearing + $0.04 NFA + $1.00 exchange = $1.64 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-energy-emini-crude",
      "name": "E-mini Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 392,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $2.40 exchange = $3.92 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-energy-gas",
      "name": "Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "NG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 472,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $3.20 exchange = $4.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-energy-emini-gas",
      "name": "E-mini Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 252,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.00 exchange = $2.52 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-energy-micro-gas",
      "name": "Micro Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MNG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 272,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.20 exchange = $2.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-energy-refined",
      "name": "Refined Products Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "RB", "HO"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 452,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $3.00 exchange = $4.52 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-metals-precious",
      "name": "Precious Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC", "SI", "PL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 472,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $3.20 exchange = $4.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-metals-base",
      "name": "Base Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "HG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 472,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $3.20 exchange = $4.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-metals-micro",
      "name": "Micro Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC", "MHG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 272,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.20 exchange = $2.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-metals-micro-silver",
      "name": "Micro Silver Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "SIL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 352,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $2.00 exchange = $3.52 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-agricultural-grains",
      "name": "Grain Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "ZC", "ZW", "ZS", "ZM", "ZL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 578,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $4.26 exchange = $5.78 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-agricultural-meats",
      "name": "Livestock Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "HE", "LE"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 572,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $4.20 exchange = $5.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-financials-notes",
      "name": "Treasury Note Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZT", "ZF"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 282,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.30 exchange = $2.82 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-financials-10yr",
      "name": "10-Year Treasury Note Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZN", "TN"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 312,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.60 exchange = $3.12 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-financials-bonds",
      "name": "Treasury Bond Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZB"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 326,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.74 exchange = $3.26 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-financials-ultra",
      "name": "Ultra Treasury Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "UB"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 342,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $1.90 exchange = $3.42 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-crypto-bitcoin",
      "name": "Micro Bitcoin Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MBT"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 382,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $2.30 exchange = $3.82 RT",
      "status": "active"
    },
    {
      "id": "topstep-tradovate-crypto-ethereum",
      "name": "Micro Ethereum Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MET"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 172,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.18 commission + $0.30 clearing + $0.04 NFA + $0.20 exchange = $1.72 RT",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "official-website",
    "sourceReference": "TopStepX-CommissionFree.md research file",
    "verifiedAt": "2025-01-19",
    "notes": "TopStep using Tradovate or TradingView platforms with all-in commission rates."
  }
}],

  ['topstep-ninjatrader', {
  "brokerId": "topstep-ninjatrader",
  "lastUpdated": "2025-01-19T00:00:00.000Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": false,
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "notes": "NinjaTrader, ATAS, or Rithmic platform included. $0.50 RT data fee included in rates."
    }
  },
  "feeConfigurations": [
    {
      "id": "topstep-ninja-equity-standard",
      "name": "Standard E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "ES", "NQ", "YM", "RTY"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 436,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $2.76 exchange = $4.36 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-equity-micro",
      "name": "Micro E-mini Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "MES", "MNQ", "MYM", "M2K"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 142,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $0.18 commission + $0.50 data + $0.04 NFA + $0.70 exchange = $1.42 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-equity-nikkei",
      "name": "Nikkei Index Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "indices"
      },
      "appliesTo": [
        "NKD"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 590,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $4.30 exchange = $5.90 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-currencies-major",
      "name": "Major Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "6A", "6B", "6C", "6E", "6J", "6S", "6N", "6M"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 480,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $3.20 exchange = $4.80 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-currencies-emini",
      "name": "E-mini Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "E7"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 330,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.70 exchange = $3.30 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-currencies-micro",
      "name": "Micro Currency Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "currencies"
      },
      "appliesTo": [
        "M6A", "M6B", "M6E"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 120,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $0.18 commission + $0.50 data + $0.04 NFA + $0.48 exchange = $1.20 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-energy-crude",
      "name": "Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "CL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 460,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $3.00 exchange = $4.60 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-energy-micro-crude",
      "name": "Micro Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MCL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 172,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $0.18 commission + $0.50 data + $0.04 NFA + $1.00 exchange = $1.72 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-energy-emini-crude",
      "name": "E-mini Crude Oil Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QM"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 400,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $2.40 exchange = $4.00 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-energy-gas",
      "name": "Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "NG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 480,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $3.20 exchange = $4.80 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-energy-emini-gas",
      "name": "E-mini Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "QG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 260,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.00 exchange = $2.60 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-energy-micro-gas",
      "name": "Micro Natural Gas Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "MNG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 280,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.20 exchange = $2.80 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-energy-refined",
      "name": "Refined Products Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "energies"
      },
      "appliesTo": [
        "RB", "HO"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 460,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $3.00 exchange = $4.60 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-metals-precious",
      "name": "Precious Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "GC", "SI", "PL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 480,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $3.20 exchange = $4.80 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-metals-base",
      "name": "Base Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "HG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 480,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $3.20 exchange = $4.80 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-metals-micro",
      "name": "Micro Metals Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "MGC", "MHG"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 280,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.20 exchange = $2.80 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-metals-micro-silver",
      "name": "Micro Silver Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "metals"
      },
      "appliesTo": [
        "SIL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 360,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $2.00 exchange = $3.60 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-agricultural-grains",
      "name": "Grain Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "ZC", "ZW", "ZS", "ZM", "ZL"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 586,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $4.26 exchange = $5.86 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-agricultural-meats",
      "name": "Livestock Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "commodities"
      },
      "appliesTo": [
        "HE", "LE"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 580,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $4.20 exchange = $5.80 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-financials-notes",
      "name": "Treasury Note Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZT", "ZF"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 290,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.30 exchange = $2.90 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-financials-10yr",
      "name": "10-Year Treasury Note Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZN", "TN"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 320,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.60 exchange = $3.20 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-financials-bonds",
      "name": "Treasury Bond Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "ZB"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 334,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.74 exchange = $3.34 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-financials-ultra",
      "name": "Ultra Treasury Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "financials"
      },
      "appliesTo": [
        "UB"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 350,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $1.90 exchange = $3.50 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-crypto-bitcoin",
      "name": "Micro Bitcoin Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MBT"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 390,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $2.30 exchange = $3.90 RT",
      "status": "active"
    },
    {
      "id": "topstep-ninja-crypto-ethereum",
      "name": "Micro Ethereum Futures",
      "category": {
        "assetClass": "futures",
        "marketSegment": "digital-assets"
      },
      "appliesTo": [
        "MET"
      ],
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 180,
        "feePrecision": 2,
        "chargeOn": "roundTrip",
        "verificationStatus": "verified"
      },
      "notes": "All-in rate: $1.06 commission + $0.50 data + $0.04 NFA + $0.20 exchange = $1.80 RT",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "official-website",
    "sourceReference": "TopStepX-CommissionFree.md research file",
    "verifiedAt": "2025-01-19",
    "notes": "TopStep using NinjaTrader, ATAS, or Rithmic platforms with all-in commission rates including data fees."
  }
}],

['paper-trading', {
  "brokerId": "paper-trading",
  "lastUpdated": "2025-08-21T00:00:00.000Z",
  "feeMetadata": {
    "supportedAccountCurrencies": [
      "USD",
      "EUR",
      "GBP",
      "CHF",
      "JPY",
      "CAD",
      "AUD"
    ],
    "defaultAccountCurrency": "USD",
    "supportsMultiCurrency": true,
    "minimumBalance": {
      "amount": 0,
      "precision": 2,
      "currency": "USD"
    },
    "platformFees": {
      "monthlyFee": 0,
      "dataFees": 0,
      "inactivityFee": 0,
      "notes": "Paper trading - all fees are zero for simulated trading environment."
    }
  },
  "feeConfigurations": [
    {
      "id": "paper-trading-all-assets",
      "name": "All Asset Classes",
      "commission": {
        "feeType": "fixedPerUnit",
        "feeAmount": 0,
        "feePrecision": 2,
        "chargeOn": "perSide",
        "verificationStatus": "verified"
      },
      "notes": "Paper trading has zero fees for all asset classes and instruments",
      "status": "active"
    }
  ],
  "dataSource": {
    "source": "official-website",
    "sourceReference": "Paper trading configuration with zero fees",
    "verifiedAt": "2025-08-21",
    "notes": "Paper trading broker with zero fees across all asset classes for simulation purposes."
  }
}]

]);

// Initialize with sample data for demonstration
// TODO: In production, this would be loaded from API or database

/**
 * Get fee details for a specific broker
 */
export const getBrokerFeeDetails = (brokerId: string): BrokerFeeCatalogEntry | null => {
  return BROKER_FEE_CATALOG_DATA.get(brokerId) || null;
};

/**
 * Check if broker has fee details available
 */
export const hasBrokerFeeDetails = (brokerId: string): boolean => {
  return BROKER_FEE_CATALOG_DATA.has(brokerId);
};

/**
 * Get all broker IDs with fee details
 */
export const getBrokerIdsWithFeeDetails = (): string[] => {
  return Array.from(BROKER_FEE_CATALOG_DATA.keys());
};

/**
 * Add or update fee details for a broker
 */
export const setBrokerFeeDetails = (feeReference: BrokerFeeCatalogEntry): void => {
  BROKER_FEE_CATALOG_DATA.set(feeReference.brokerId, {
    ...feeReference,
    lastUpdated: new Date().toISOString()
  });
};

/**
 * Remove fee details for a broker
 */
export const removeBrokerFeeDetails = (brokerId: string): boolean => {
  return BROKER_FEE_CATALOG_DATA.delete(brokerId);
};

/**
 * Get fee configurations by asset class for a specific broker
 */
export const getBrokerFeeConfigurationsByAssetClass = (
  brokerId: string, 
  assetClass: string
) => {
  const brokerFees = getBrokerFeeDetails(brokerId);
  if (!brokerFees) return [];
  
  return brokerFees.feeConfigurations.filter(config => 
    config.category?.assetClass === assetClass
  );
};

/**
 * Search fee configurations across all brokers
 */
export const searchFeeConfigurations = (searchTerm: string) => {
  if (!searchTerm.trim()) return [];
  
  const results: Array<{
    brokerId: string;
    brokerName: string;
    configuration: BrokerFeeConfiguration;
  }> = [];
  
  const searchLower = searchTerm.toLowerCase();
  
  // Search through all broker fee data
  for (const [brokerId, brokerFees] of BROKER_FEE_CATALOG_DATA.entries()) {
    brokerFees.feeConfigurations.forEach(config => {
      // Search in config name, notes, and appliesTo instruments
      const matchesName = config.name.toLowerCase().includes(searchLower);
      const matchesNotes = config.notes?.toLowerCase().includes(searchLower);
      const matchesInstruments = config.appliesTo?.some(instrument => 
        instrument.toLowerCase().includes(searchLower)
      );
      const matchesCategory = config.category?.assetClass?.toLowerCase().includes(searchLower) ||
                            config.category?.marketSegment?.toLowerCase().includes(searchLower);
      
      if (matchesName || matchesNotes || matchesInstruments || matchesCategory) {
        results.push({
          brokerId,
          brokerName: brokerFees.brokerId, // Use brokerId as name for now
          configuration: config
        });
      }
    });
  }
  
  return results;
};